import {Box, Col, DSIconBell, DSIconButton, DSIconChevronDown, Row, css} from "@cdx/ds";
import {Card} from "../../cdx-models/Card";
import {NoneButton} from "./shared";
import {ArrowOverlay, DropDownForChild, TooltipForChild, cx, errorToString} from "@cdx/common";
import {Root} from "../../cdx-models/Root";
import {lazy} from "react";
import {countWorkdaysForAccount, dateToDay, dayToDate, superShortDate} from "../../lib/date-utils";
import messenger from "../../lib/messenger";
import {getLimits} from "../../features/billing/billing-utils";
import {dsListTileStyles} from "@cdx/ds/components/DSListTile/DSListTile.css";
import Helper from "../Helper";
import {pluralize} from "../../lib/utils";

const dueDateHelper = (
  <Helper heading="What are Due Dates?">
    <p>
      <i>Due Dates</i> represent important invidual deadlines. Beyond Milestones and Runs they
      represent an even more fine-granular target date for a specific card.
    </p>

    <p>Milestones, Runs and Due Dates can be used in any combination.</p>
  </Helper>
);

const DayPicker = lazy(
  () => import(/* webpackChunkName: "DayPicker" */ "../../components/DayPicker")
);

type DueDateOverlayProps = {
  card: Card;
  onChange: (props: any) => unknown;
  overlayProps: any;
};
const DueDateOverlay = ({card, onChange, overlayProps}: DueDateOverlayProps) => {
  const handleChange = (day: Date | null) => {
    Promise.resolve(onChange(day && dateToDay(day))).then(
      () => overlayProps.close(),
      (e) => messenger.send(errorToString(e), {type: "error"})
    );
  };
  return (
    <ArrowOverlay bg="white" arrowSize="sm" {...overlayProps}>
      <Col divideX>
        <Row align="center" px="24px" py="16px">
          <Box as="h2" size={14}>
            Pick due date
          </Box>
          <NoneButton
            active={!card.dueDate}
            onClick={() => handleChange(null)}
            className={css({ml: "auto"})}
          />
        </Row>
        <Col px="12px" py="8px">
          <DayPicker
            onSelect={handleChange}
            disabledDays={{before: new Date()}}
            firstDayOfWeek={1}
            numberOfMonths={2}
            fromDate={new Date()}
            selected={card.dueDate && dayToDate(card.dueDate)}
            mode="single"
          />
        </Col>
      </Col>
    </ArrowOverlay>
  );
};

type SprintPropPickerNextProps = {
  card: Card;
  root: Root;
  onChange: (props: any) => unknown;
};
const DueDatePropPicker = (props: SprintPropPickerNextProps) => {
  const {card, onChange, root} = props;

  const limits = getLimits(root.account);
  const dueDatesEnabled = limits.withDueDates === "true";

  const getTooltip = () => {
    if (card.dueDate) {
      if (dueDatesEnabled) {
        return "Click th change due date.";
      } else {
        return "This card has a disabled due date. Upgrade to the Pro plan to use this feature.";
      }
    }

    if (dueDatesEnabled) {
      return "Click to pick a due date.";
    } else {
      return "Upgrade to the Pro plan to get access to Due dates. ";
    }
  };

  const disabled = !dueDatesEnabled && !card.dueDate;

  const asDate = card.dueDate && dayToDate(card.dueDate);

  const workDays = card.dueDate
    ? countWorkdaysForAccount({
        account: root.account,
        targetDay: asDate,
      })
    : null;

  const getType = () => {
    if (workDays === null) return dsListTileStyles.theme.active;
    if (workDays <= 0) return dsListTileStyles.theme.alert;
    if (workDays <= 5) return dsListTileStyles.theme.warn;
    return dsListTileStyles.theme.active;
  };

  return (
    <Col sp="12px" pt="16px" pb={card.dueDate ? "24px" : "16px"}>
      <Row align="center" sp="8px">
        <Box as="h3" size={12} bold color="primary" textTransform="uppercase">
          Due Date
        </Box>
        {dueDateHelper}
        <DropDownForChild
          setChildProps={({isOpen}: any) => ({
            tooltip: isOpen ? null : getTooltip(),
            active: isOpen,
          })}
          renderOverlay={(overlayProps: any) => (
            <DueDateOverlay
              overlayProps={overlayProps}
              onChange={(date) => onChange({dueDate: date})}
              card={card}
            />
          )}
          overlayProps={{placement: "right", distanceFromAnchor: 10}}
        >
          <TooltipForChild tooltip={null} targetIsDisabled={disabled}>
            <DSIconButton
              variant="secondary"
              size="sm"
              className={css({ml: "auto"})}
              label="Pick"
              icon={<DSIconChevronDown />}
              iconPosition="right"
              disabled={disabled}
            />
          </TooltipForChild>
        </DropDownForChild>
        <NoneButton onClick={() => onChange({dueDate: null})} active={!card.dueDate} />
      </Row>
      {card.dueDate && (
        <div className={cx(dsListTileStyles.base, dsListTileStyles.size.default, getType())}>
          <Row sp="8px" align="center">
            <DSIconBell
              size={20}
              className={
                cx()
                // dueDateCardHintStyles.container.base,
              }
            />
            <Box weight="normal">
              {workDays! < 0 ? (
                <>
                  Overdue since <b>{pluralize(-workDays!, "workday")}</b>
                </>
              ) : (
                <>
                  Due in <b>{pluralize(workDays!, "workday")}</b>
                </>
              )}{" "}
              on <b>{superShortDate(asDate)}</b>
            </Box>
          </Row>
        </div>
      )}
    </Col>
  );
};
export default DueDatePropPicker;
